// src/api/profileAPI.js

import api from "./api";
import {
  MY_PROFILE_INSTANT_KEY,
  buildMyProfileInstantKey,
  buildPublicProfileInstantKey,
  clearProfileInstantSnapshot,
  writeProfileInstantSnapshot,
} from "../utils/profile/profileInstantCache";

const PROFILE_CACHE_TTL_MS = 45 * 1000;

const myProfileCache = new Map();
const publicProfileCache = new Map();

function isFresh(expiresAt) {
  return Number(expiresAt || 0) > Date.now();
}

function normalizeUsername(username = "") {
  return String(username || "").trim().toLowerCase();
}

function getUserCacheKey(user = null) {
  const id = user?.id || user?.user_id || user?.userId;
  if (id) return `id:${id}`;

  const username = normalizeUsername(user?.username);
  if (username) return `username:${username}`;

  return "";
}

function getPayloadCacheKey(payload = null) {
  return getUserCacheKey(payload?.user);
}

export function primeMyProfileCache(payload) {
  if (!payload?.user) return;

  const cacheKey = getPayloadCacheKey(payload);
  if (!cacheKey) return;

  myProfileCache.set(cacheKey, {
    data: payload,
    expiresAt: Date.now() + PROFILE_CACHE_TTL_MS,
  });

  writeProfileInstantSnapshot(buildMyProfileInstantKey(payload.user), payload);
}

export function invalidateMyProfileCache(user = null) {
  if (user) {
    const cacheKey = getUserCacheKey(user);
    if (cacheKey) {
      myProfileCache.delete(cacheKey);
      clearProfileInstantSnapshot(buildMyProfileInstantKey(user));
    }
    return;
  }

  myProfileCache.clear();
  clearProfileInstantSnapshot(MY_PROFILE_INSTANT_KEY);
}

export async function getMyProfileCached(options = {}) {
  const forceFresh = options.forceFresh === true;
  const currentUser = options.currentUser || null;
  const currentCacheKey = getUserCacheKey(currentUser);

  if (!forceFresh && currentCacheKey) {
    const cached = myProfileCache.get(currentCacheKey);
    if (cached?.data && isFresh(cached.expiresAt)) {
      return cached.data;
    }
  }

  const res = await api.get("/api/users/profile", {
    params: forceFresh ? { _ts: Date.now() } : undefined,
    headers: forceFresh
      ? {
          "Cache-Control": "no-cache",
          Pragma: "no-cache",
        }
      : undefined,
  });

  primeMyProfileCache(res.data);
  return res.data;
}

export function primePublicProfileCache(username, payload) {
  const key = normalizeUsername(username || payload?.user?.username);
  if (!key || !payload?.user) return;

  publicProfileCache.set(key, {
    data: payload,
    expiresAt: Date.now() + PROFILE_CACHE_TTL_MS,
  });

  writeProfileInstantSnapshot(buildPublicProfileInstantKey(key), payload);
}

export function invalidatePublicProfileCache(username = "") {
  const key = normalizeUsername(username);
  if (!key) return;

  publicProfileCache.delete(key);
  clearProfileInstantSnapshot(buildPublicProfileInstantKey(key));
}

export async function getPublicProfileByUsernameCached(username, options = {}) {
  const key = normalizeUsername(username);
  const forceFresh = options.forceFresh === true;

  if (!key) return null;

  const cached = publicProfileCache.get(key);
  if (!forceFresh && cached?.data && isFresh(cached.expiresAt)) {
    return cached.data;
  }

  const res = await api.get(
    `/api/users/public/by-username/${encodeURIComponent(key)}`,
    {
      params: forceFresh ? { _ts: Date.now() } : undefined,
      headers: forceFresh
        ? {
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
          }
        : undefined,
    }
  );

  primePublicProfileCache(key, res.data);
  return res.data;
}